// PlanBadge renders an account's billing tier. Plan IDs are stable backend
// strings (free / starter / pro / nova). The id↔label split is kept here so the
// display name can diverge from the id without touching the backend.
//
// The chip is a solid neutral grey (bg-gruvbox-bg3) carrying only the plan color
// in its text + ring, so it stays legible regardless of the parent surface — the
// switcher's selected row paints a gold highlight, and a translucent chip would
// dissolve into it. Paid plans carry a crisp accent token (gold-bright / --nova
// teal) — the old gruvbox sage-aqua read muddy, especially next to PRO's gold.
const PLAN_DISPLAY: Record<string, { label: string; accent: string | null; paid: boolean }> = {
  free: { label: "FREE", accent: null, paid: false },
  starter: { label: "STARTER", accent: null, paid: false },
  pro: { label: "PRO", accent: "var(--gold-bright)", paid: true },
  nova: { label: "NOVA", accent: "var(--nova)", paid: true },
};

const norm = (plan: string | undefined) => (plan || "free").toLowerCase();

export function planLabel(plan: string | undefined): string {
  return (PLAN_DISPLAY[norm(plan)] ?? PLAN_DISPLAY.free).label;
}

// isPaidPlan gates the compact surfaces (e.g. the galaxy switcher) so only a
// paying account carries a badge — that's what gives it status.
export function isPaidPlan(plan: string | undefined): boolean {
  return (PLAN_DISPLAY[norm(plan)] ?? PLAN_DISPLAY.free).paid;
}

export function PlanBadge({ plan }: { plan: string | undefined }) {
  const p = PLAN_DISPLAY[norm(plan)] ?? PLAN_DISPLAY.free;
  return (
    <span
      className="rounded-full bg-gruvbox-bg3 px-md py-xs text-2xs font-bold tracking-wide"
      style={
        p.accent
          ? { color: p.accent, boxShadow: `inset 0 0 0 1px color-mix(in oklab, ${p.accent} 45%, transparent)` }
          : { color: "var(--text-muted)" }
      }
    >
      {p.label}
    </span>
  );
}
